// HeicService.js
import heic2any from "heic2any";
import { MODALITY } from "../classes/enums/MedicalImagesEnum";
import { dbDateFormat } from "../classes/utils/DateUtils";

class HeicService {
  /**
   * Converts a HEIC file to JPEG format
   * @param {File} heicFile - The HEIC file to convert
   * @returns {Promise<File>} - A Promise that resolves to a JPEG File object
   */
  static async convertToJpeg(heicFile) {
    try {
      // Convert the HEIC to a JPEG blob
      const jpegBlob = await heic2any({
        blob: heicFile,
        toType: "image/jpeg",
        quality: 0.9, // High quality
      });

      // Create a new File object with the JPEG blob
      const jpegFile = new File(
        [jpegBlob],
        `${heicFile.name.replace(/\.heic$/i, "")}.jpg`,
        { type: "image/jpeg", lastModified: heicFile.lastModified }
      );

      return jpegFile;
    } catch (error) {
      throw new Error("Failed to convert HEIC file to JPEG");
    }
  }

  /**
   * Reads metadata from a HEIC file
   * @param {File} file - The HEIC file
   * @returns {Promise<Object>} - A Promise that resolves to metadata extracted from the file
   */
  static async readMetadata(file) {
    try {
      // Extract creation date from file's lastModified property
      const creationDate = new Date(file.lastModified);

      // For HEIC files, we'll set modality to "MO" (Morphology) like other image formats
      const modality = MODALITY.MO;

      // Create a unique identifier for this file
      const seriesId = `HEIC_${file.name}_${file.lastModified}`;

      return {
        creationDate: !isNaN(creationDate.getTime())
          ? dbDateFormat(creationDate)
          : null,
        modality,
        seriesId,
        // We could extract more metadata with specialized HEIC metadata extraction libraries
        // but for now we'll keep it simple
        patientInfo: {
          firstName: null,
          lastName: null,
          birthDate: null,
          gender: null,
        },
      };
    } catch (error) {
      return {
        creationDate: null,
        modality: MODALITY.MO,
        seriesId: null,
        patientInfo: {
          firstName: null,
          lastName: null,
          birthDate: null,
          gender: null,
        },
      };
    }
  }
}

export default HeicService;
